import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { P } from '@/auth/permissions'
import { i18n } from '@/i18n'
import { useSessionStore } from '@/stores/session'

/**
 * Routes declare the permission they need. The guard below hides what a user
 * cannot reach, and the same permissions are enforced by the service - this is
 * navigation, not security.
 */
declare module 'vue-router' {
  interface RouteMeta {
    /** A bundle key, not a phrase: the title has to follow the language. */
    titleKey: string
    /** Any one of these is enough to enter. */
    permissions?: string[]
    /** Shown in the sidebar under this group. */
    group?: 'Operations' | 'Customers' | 'Marketing' | 'Insight' | 'Configuration'
    icon?: string
    hidden?: boolean
  }
}

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    name: 'dashboard',
    component: () => import('@/views/DashboardView.vue'),
    meta: { titleKey: 'nav.dashboard', icon: 'home', permissions: [P.ANALYTICS_READ] },
  },

  /* Operations - what a cashier or agent does all day. */
  {
    path: '/cashier',
    name: 'cashier',
    component: () => import('@/views/counter/CashierView.vue'),
    meta: { titleKey: 'nav.cashier', group: 'Operations', icon: 'store', permissions: [P.COUNTER_USE] },
  },
  {
    path: '/earn',
    name: 'earn',
    component: () => import('@/views/operations/EarnView.vue'),
    meta: { titleKey: 'nav.earn', group: 'Operations', icon: 'plus', permissions: [P.POINTS_EARN] },
  },
  // Redeeming is the counter's job now: app approval, the app code, an SMS
  // code as the fallback, the member discount and an order reference, all in
  // one place. The old OTP-only page offered none of that, so its address
  // opens the counter instead.
  { path: '/redeem', redirect: '/cashier' },
  {
    path: '/refunds',
    name: 'refunds',
    component: () => import('@/views/RefundsView.vue'),
    meta: { titleKey: 'nav.refunds', group: 'Operations', icon: 'undo', permissions: [P.REFUND_READ] },
  },
  {
    path: '/transactions',
    name: 'transactions',
    component: () => import('@/views/TransactionsView.vue'),
    meta: { titleKey: 'nav.transactions', group: 'Operations', icon: 'list', permissions: [P.TRANSACTION_READ] },
  },

  /* Customers */
  {
    path: '/customers',
    name: 'customers',
    component: () => import('@/views/customers/CustomerListView.vue'),
    meta: { titleKey: 'nav.customers', group: 'Customers', icon: 'users', permissions: [P.CUSTOMER_READ] },
  },
  {
    path: '/customers/:id',
    name: 'customer-detail',
    component: () => import('@/views/customers/CustomerDetailView.vue'),
    props: true,
    meta: { titleKey: 'nav.customerDetail', permissions: [P.CUSTOMER_READ], hidden: true },
  },
  {
    path: '/segments',
    name: 'segments',
    component: () => import('@/views/SegmentsView.vue'),
    meta: { titleKey: 'nav.segments', group: 'Customers', icon: 'layers', permissions: [P.SEGMENT_READ] },
  },
  {
    path: '/segment-applications',
    name: 'segment-applications',
    component: () => import('@/views/SegmentApplicationsView.vue'),
    meta: {
      titleKey: 'nav.segmentApplications', group: 'Customers', icon: 'shield',
      permissions: [P.SEGMENT_APPLICATION_REVIEW],
    },
  },
  {
    path: '/bulk-imports',
    name: 'bulk-imports',
    component: () => import('@/views/BulkImportsView.vue'),
    meta: { titleKey: 'nav.bulkImports', group: 'Customers', icon: 'upload', permissions: [P.BULK_IMPORT_READ] },
  },

  /* Marketing */
  {
    path: '/vouchers',
    name: 'vouchers',
    component: () => import('@/views/vouchers/VouchersView.vue'),
    meta: { titleKey: 'nav.vouchers', group: 'Marketing', icon: 'tag', permissions: [P.VOUCHER_READ] },
  },
  {
    path: '/vouchers/codes',
    name: 'voucher-codes',
    component: () => import('@/views/vouchers/VoucherCodesView.vue'),
    meta: { titleKey: 'nav.voucherCodes', group: 'Marketing', icon: 'key', permissions: [P.VOUCHER_READ] },
  },
  {
    path: '/cashback',
    name: 'cashback',
    component: () => import('@/views/CashbackView.vue'),
    meta: { titleKey: 'nav.cashback', group: 'Marketing', icon: 'refresh', permissions: [P.CASHBACK_READ] },
  },
  {
    path: '/campaigns',
    name: 'campaigns',
    component: () => import('@/views/CampaignsView.vue'),
    meta: { titleKey: 'nav.campaigns', group: 'Marketing', icon: 'message', permissions: [P.CAMPAIGN_READ] },
  },

  /* Insight */
  {
    path: '/reports',
    name: 'reports',
    component: () => import('@/views/ReportsView.vue'),
    meta: { titleKey: 'nav.reports', group: 'Insight', icon: 'chart', permissions: [P.REPORT_READ] },
  },

  {
    path: '/counter/report',
    name: 'counter-report',
    component: () => import('@/views/counter/CounterReportView.vue'),
    meta: { titleKey: 'nav.counterReport', group: 'Insight', icon: 'chart', permissions: [P.COUNTER_REPORT] },
  },

  /* Configuration */
  {
    path: '/program',
    name: 'program',
    component: () => import('@/views/ProgramView.vue'),
    meta: { titleKey: 'nav.program', group: 'Configuration', icon: 'sliders', permissions: [P.PROGRAM_READ] },
  },
  {
    path: '/notifications',
    name: 'notifications',
    component: () => import('@/views/NotificationsView.vue'),
    meta: { titleKey: 'nav.notifications', group: 'Configuration', icon: 'bell', permissions: [P.NOTIFICATION_READ] },
  },
  {
    path: '/sms/providers',
    name: 'sms-providers',
    component: () => import('@/views/sms/SmsProvidersView.vue'),
    meta: { titleKey: 'nav.smsProviders', group: 'Configuration', icon: 'message', permissions: [P.SMS_PROVIDER_READ] },
  },
  {
    path: '/sms/log',
    name: 'sms-log',
    component: () => import('@/views/sms/SmsLogView.vue'),
    meta: { titleKey: 'nav.smsLog', group: 'Configuration', icon: 'inbox', permissions: [P.SMS_LOG_READ] },
  },
  {
    path: '/counter/settings',
    name: 'counter-settings',
    component: () => import('@/views/counter/CounterSettingsView.vue'),
    meta: { titleKey: 'nav.counterSettings', group: 'Configuration', icon: 'store', permissions: [P.COUNTER_ADMIN] },
  },
  {
    path: '/users',
    name: 'users',
    component: () => import('@/views/iam/UsersView.vue'),
    meta: { titleKey: 'nav.users', group: 'Configuration', icon: 'user', permissions: [P.IAM_USER_READ] },
  },
  {
    path: '/roles',
    name: 'roles',
    component: () => import('@/views/iam/RolesView.vue'),
    meta: { titleKey: 'nav.roles', group: 'Configuration', icon: 'shield', permissions: [P.IAM_ROLE_READ] },
  },

  {
    path: '/no-access',
    name: 'no-access',
    component: () => import('@/views/NoAccessView.vue'),
    meta: { titleKey: 'nav.noAccess', hidden: true },
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/views/NotFoundView.vue'),
    meta: { titleKey: 'nav.notFound', hidden: true },
  },
]

export const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior: () => ({ top: 0 }),
})

router.beforeEach(async (to) => {
  const session = useSessionStore()
  if (!session.user && !session.loading) {
    await session.load()
  }

  const required = to.meta.permissions
  if (required && required.length > 0 && !session.canAny(...required)) {
    // Send them somewhere they can actually use rather than a dead end.
    const landing = firstReachable(session)
    return landing && landing !== to.name ? { name: landing } : { name: 'no-access' }
  }

  const title = to.meta.titleKey ? i18n.global.t(to.meta.titleKey) : null
  document.title = title ? `${title} · Loyalty Portal` : 'Loyalty Portal'
  return true
})

/** The first route in the menu this user can open. */
function firstReachable(session: ReturnType<typeof useSessionStore>): string | null {
  for (const route of routes) {
    if (route.meta?.hidden || !route.name) continue
    const required = route.meta?.permissions
    if (!required || required.length === 0 || session.canAny(...required)) {
      return String(route.name)
    }
  }
  return null
}

export { routes }
